import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Flower2, ArrowRight } from 'lucide-react';
import { FaLeaf, FaTruck, FaCalendarAlt, FaSeedling, FaSync, FaBuilding, FaPhoneAlt, FaCheckCircle } from 'react-icons/fa';
import { MdLocalFlorist } from 'react-icons/md';
import './ServiceDetail.css';

const ACCENT = '#558b2f';
const ACCENT_LIGHT = '#8bc34a';
const ACCENT_RGB = '85,139,47';

const benefits = [
  {
    emoji: <FaLeaf size={22} />,
    title: 'Healthy Premium Plants',
    desc: 'Well-maintained indoor and outdoor plants selected for offices, homes, events, and commercial spaces.'
  },
  {
    emoji: <FaTruck size={22} />,
    title: 'Delivery & Professional Setup',
    desc: 'Complete plant delivery, placement, and setup service for a hassle-free experience.'
  },
  {
    emoji: <FaCalendarAlt size={22} />,
    title: 'Flexible Rental Plans',
    desc: 'Short-term and long-term plant rental options designed for different requirements and budgets.'
  },
  {
    emoji: <MdLocalFlorist size={22} />,
    title: 'Decorative Planter Options',
    desc: 'Choose from stylish pots and planters that complement your interior or event theme.'
  },
  {
    emoji: <FaSync size={22} />,
    title: 'Plant Replacement Support',
    desc: 'Quick replacement and seasonal plant updates to keep your space fresh and attractive.'
  },
  {
    emoji: <FaBuilding size={22} />,
    title: 'Corporate & Event Solutions',
    desc: 'Specialized plant rental services for offices, exhibitions, hotels, conferences, and celebrations.'
  },
];

const steps = [
  {
    num: '01',
    icon: <FaPhoneAlt size={20} />,
    title: 'Discuss Your Requirement',
    desc: 'Share your space, event, or office plant rental requirements with our team.'
  },
  {
    num: '02',
    icon: <FaLeaf size={20} />,
    title: 'Select Plants & Planters',
    desc: 'Choose suitable plants and decorative planters based on your space and style.'
  },
  {
    num: '03',
    icon: <FaTruck size={20} />,
    title: 'Delivery & Installation',
    desc: 'Our team delivers and professionally arranges the plants at your location.'
  },
  {
    num: '04',
    icon: <MdLocalFlorist size={20} />,
    title: 'Enjoy Ongoing Support',
    desc: 'Receive maintenance assistance and plant replacement support whenever required.'
  },
];

const stats = [
  { val: <FaLeaf size={28} />, label: 'Indoor Plants' },
  { val: <MdLocalFlorist size={28} />, label: 'Rental Solutions' },
  { val: <FaBuilding size={28} />, label: 'Corporate Spaces' },
  { val: <FaSeedling size={28} />, label: 'Event Decoration' },
];

export default function PlantRentalPage() {
  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = 'Plant Rental Service — Nisarg Nursery';
    const observer = new IntersectionObserver(
      (entries) => entries.forEach(e => { if (e.isIntersecting) e.target.classList.add('visible'); }),
      { threshold: 0.12 }
    );
    document.querySelectorAll('.sdp-observe').forEach(el => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  return (
    <div
      className="sdp-wrapper"
      style={{ '--accent': ACCENT, '--accent-light': ACCENT_LIGHT, '--accent-dark': '#4a148c', '--accent-rgb': ACCENT_RGB }}
    >
      {/* ════ HERO ════ */}
      <section className="sdp-hero">
        <div className="sdp-hero-bg">
          <img src="/images/fiddle.png" alt="Plant Rental" className="sdp-hero-img" />
          <div className="sdp-hero-overlay" />
        </div>
        <div className="sdp-hero-content">
          <nav className="sdp-breadcrumb">
            <Link to="/">Home</Link> › <Link to="/services">Services</Link> › <span>Plant Rental</span>
          </nav>
          <div className="sdp-service-tag">
            <Flower2 size={16} /> <FaLeaf /> Plant Rental Services
          </div>
          <span className="sdp-hero-icon"><Flower2 size={56} strokeWidth={1.5} /></span>
          <h1 className="sdp-hero-title">
            Professional <span className="accent-word">Plant Rental For</span> Homes, Offices &amp; Events
          </h1>
          <p className="sdp-hero-desc">
            Enhance your space with indoor and outdoor plant rental services. Nisarg Nursery provides healthy plants, decorative planters, delivery, setup, and maintenance for homes, offices, hotels, exhibitions, and special events
          </p>
          <div className="sdp-hero-cta">
            <Link to="/contact" className="sdp-btn-primary">
              Enquire Now <ArrowRight size={16} />
            </Link>
            <Link to="/services" className="sdp-btn-outline">← Back to Services</Link>
          </div>
        </div>
        <div className="sdp-hero-scroll">
          <span>SCROLL</span>
          <div className="sdp-scroll-line" />
        </div>
      </section>

      {/* ── STATS ── */}
      <div className="sdp-stats">
        {stats.map(s => (
          <div key={s.label} className="sdp-stat sdp-observe">
            <span className="sdp-stat-val">{s.val}</span>
            <span className="sdp-stat-label">{s.label}</span>
          </div>
        ))}
      </div>

      {/* ── OVERVIEW ── */}
      <section className="sdp-section sdp-overview">
        <div className="sdp-section-inner">
          <div className="sdp-overview-grid">
            <div className="sdp-overview-left sdp-observe">
              <div className="sdp-overview-img-wrap">
                <img src="/images/Amla Fruit.jpg" alt="Plant Rental" className="sdp-overview-img" />
                <div className="sdp-overview-badge">
                  <div className="sdp-overview-badge-dot" />
                  Available for Rental Now
                </div>
              </div>
            </div>
            <div className="sdp-overview-right sdp-observe">
              <div className="sdp-section-tag"><FaLeaf /> Green Rental Solutions</div>
              <h2 className="sdp-section-title">Plant Rental Services For<br />Modern Spaces & Events</h2>
              <div className="sdp-overview-text">
                <p>Our plant rental services are ideal for offices, commercial spaces, hotels, showrooms, exhibitions, weddings, and special events. We help create refreshing green environments without the long-term commitment of plant ownership.</p>
                <p>From elegant indoor plants and decorative planters to complete plant maintenance, our team manages delivery, setup, replacement, and care, ensuring your space always looks vibrant and welcoming.</p>
              </div>
              <div className="sdp-features-list">
                {[
                  'Office Plant Rental',
                  'Event Plant Rental',
                  'Indoor Plants',
                  'Decorative Planters',
                  'Maintenance Service',
                  'Flexible Rental Plans'
                ].map(f => (
                  <div key={f} className="sdp-feature-item">
                    <div className="sdp-feature-icon"><FaCheckCircle /></div>
                    {f}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── BENEFITS ── */}
      <section className="sdp-section sdp-observe">
        <div className="sdp-section-inner">
          <div className="sdp-section-tag"><FaCheckCircle /> Why Choose Our Plant Rental</div>
          <h2 className="sdp-section-title">Benefits Of Professional<br />Plant Rental Services</h2>
          <div className="sdp-benefits-grid">
            {benefits.map(b => (
              <div key={b.title} className="sdp-benefit-card sdp-observe">
                <span className="sdp-benefit-emoji">{b.emoji}</span>
                <h3 className="sdp-benefit-title">{b.title}</h3>
                <p className="sdp-benefit-desc">{b.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── HOW IT WORKS ── */}
      <section className="sdp-section sdp-process sdp-observe">
        <div className="sdp-section-inner">
          <div className="sdp-section-tag"><FaSync /> How It Works</div>
          <h2 className="sdp-section-title">Simple Process For<br />Plant Rental & Setup</h2>
          <div className="sdp-process-steps">
            {steps.map(s => (
              <div key={s.num} className="sdp-step sdp-observe">
                <div className="sdp-step-num">{s.num}</div>
                <div className="sdp-step-icon">{s.icon}</div>
                <h3 className="sdp-step-title">{s.title}</h3>
                <p className="sdp-step-desc">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA ── */}
      <div className="sdp-cta sdp-observe">
        <span className="sdp-cta-icon"><MdLocalFlorist size={28} /></span>
        <h2 className="sdp-cta-title">Ready To Transform Your Space With Plants?</h2>
        <p className="sdp-cta-desc">Contact Nisarg Nursery today for office plant rental, event plant decoration, indoor plant rental, and customized green solutions for any space.</p>
        <div className="sdp-cta-btns">
          <Link to="/contact" className="sdp-btn-primary">
            Get a Rental Quote
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M5 12h14M12 5l7 7-7 7" /></svg>
          </Link>
          <Link to="/services" className="sdp-btn-outline">← All Services</Link>
        </div>
      </div>
    </div>
  );
}
